<template>
  <v-app id="inspire">
    <v-container fluid class="therapy-hours-page">
      <div class="toolbar-panel">
        <v-row align="center" dense class="toolbar-controls-row">
          <v-col cols="12" md="6" lg="5">
            <v-select
              v-model="selectedMonths"
              :items="monthItems"
              :menu-props="{ maxHeight: 360 }"
              label="Mesi"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              outlined
              hide-details
              clearable
              clear-icon="mdi-restore"
              class="months-select"
              @change="onMonthsChange"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index < 3" small>
                  {{ item.text }}
                </v-chip>
                <span v-else-if="index === 3" class="selection-more">
                  +{{ selectedMonths.length - 3 }}
                </span>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" sm="4" md="2" lg="2">
            <v-select
              v-model="selectedYear"
              :items="years"
              label="Anno"
              dense
              outlined
              hide-details
              @change="fetchSummary"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="auto" class="d-flex justify-end toolbar-actions">
            <v-btn-toggle v-model="viewMode" mandatory dense>
              <v-btn value="cards" title="Vista card" aria-label="Vista card">
                <v-icon small>mdi-card-bulleted-outline</v-icon>
              </v-btn>
              <v-btn value="table" title="Vista tabella" aria-label="Vista tabella">
                <v-icon small>mdi-table</v-icon>
              </v-btn>
            </v-btn-toggle>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :loading="loading" v-bind="attrs" v-on="on" @click="fetchSummary">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Aggiorna</span>
            </v-tooltip>
          </v-col>
        </v-row>

        <v-row dense class="toolbar-summary-row">
          <v-col cols="12" class="d-flex flex-wrap align-center summary-chips">
            <v-chip label small color="teal" text-color="white">
              Attese {{ formatHours(totalExpectedHours) }} h
            </v-chip>
            <v-chip label small color="blue-grey" text-color="white">
              Erogate {{ formatHours(totalDeliveredHours) }} h
            </v-chip>
            <v-chip label small :color="totalDeltaColor" text-color="white">
              Scostamento {{ formatSignedHours(totalDeltaHours) }} h
            </v-chip>
            <v-chip label small color="grey lighten-3">
              {{ periodLabel }}
            </v-chip>
          </v-col>
        </v-row>
      </div>

      <div v-if="viewMode === 'cards'" class="cards-grid">
        <v-card
          v-for="item in rows"
          :key="item.pazienteId"
          outlined
          class="patient-card"
          :class="statusClass(item.stato)"
        >
          <div class="card-header">
            <div class="patient-cell">
              <strong>{{ item.pazienteCognome }} {{ item.pazienteNome }}</strong>
              <v-chip v-if="item.privata" x-small label color="grey lighten-3">Privato</v-chip>
            </div>
            <v-chip small :color="statusColor(item.stato)" :text-color="statusTextColor(item.stato)">
              {{ statusText(item.stato) }}
            </v-chip>
          </div>

          <div class="card-metrics">
            <div class="metric">
              <span>Attese</span>
              <strong>{{ formatHours(expectedHours(item)) }} h</strong>
              <small>{{ formatHours(item.settimaneEquivalenti) }} sett. eq.</small>
            </div>
            <div class="metric">
              <span>Erogate</span>
              <strong>{{ formatHours(item.oreTotaliErogate) }} h</strong>
              <small>{{ formatPercent(item.percentuale) }}</small>
            </div>
            <div class="metric">
              <span>Scostamento</span>
              <strong :class="deltaClass(item.scostamentoOre)">
                {{ formatSignedHours(item.scostamentoOre) }} h
              </strong>
              <small>Terapia {{ formatHours(item.oreTerapiaErogate) }} h</small>
            </div>
          </div>

          <v-progress-linear
            :value="progressValue(item)"
            :color="progressColor(item.stato)"
            height="8"
            rounded
            class="card-progress"
          ></v-progress-linear>

          <div class="card-footer">
            <div class="hours-editor">
              <v-text-field
                v-model="item.draftOreTerapia"
                type="number"
                min="0"
                step="1"
                dense
                outlined
                hide-details
                label="Ore sett."
                class="hours-input"
                @input="markDirty(item)"
                @keyup.enter="saveOre(item)"
              ></v-text-field>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    :loading="item.saving"
                    :disabled="!item.dirty || item.saving"
                    v-bind="attrs"
                    v-on="on"
                    @click="saveOre(item)"
                  >
                    <v-icon small>mdi-content-save</v-icon>
                  </v-btn>
                </template>
                <span>Salva ore settimanali</span>
              </v-tooltip>
            </div>

            <v-btn icon small @click="item.expanded = !item.expanded">
              <v-icon>{{ item.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
          </div>

          <v-expand-transition>
            <div v-show="item.expanded" class="detail-panel">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Settimana</th>
                    <th>Periodo</th>
                    <th class="text-right">Quota</th>
                    <th class="text-right">Attese</th>
                    <th class="text-right">Erogate</th>
                    <th class="text-right">Scostamento</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="week in item.dettagliSettimanali" :key="week.inizioSettimana + '-' + week.fineNelPeriodo">
                    <td>{{ formatDateRange(week.inizioSettimana, week.fineSettimana) }}</td>
                    <td>{{ formatDetailRange(week) }}</td>
                    <td class="text-right">{{ week.giorniNelMese }} gg</td>
                    <td class="text-right">{{ formatHours(week.oreAttese) }} h</td>
                    <td class="text-right">{{ formatHours(week.oreTotali) }} h</td>
                    <td class="text-right" :class="deltaClass(week.scostamentoOre)">
                      {{ formatSignedHours(week.scostamentoOre) }} h
                    </td>
                    <td>
                      <v-chip x-small :color="statusColor(week.stato)" :text-color="statusTextColor(week.stato)">
                        {{ statusText(week.stato) }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div class="type-breakdown">
                <v-chip x-small label color="teal" text-color="white">
                  Terapia {{ formatHours(item.oreTerapiaErogate) }} h
                </v-chip>
                <v-chip x-small label color="light-green lighten-3">
                  Supervisione {{ formatHours(item.oreSupervisione) }} h
                </v-chip>
                <v-chip x-small label color="light-blue lighten-3">
                  Logopedista {{ formatHours(item.oreLogopedista) }} h
                </v-chip>
                <v-chip x-small label color="blue-grey lighten-4">
                  Totali {{ formatHours(item.oreTotaliErogate) }} h
                </v-chip>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </div>

      <v-data-table
        v-else
        :headers="headers"
        :items="rows"
        :loading="loading"
        item-key="pazienteId"
        show-expand
        disable-sort
        hide-default-footer
        :items-per-page="-1"
        class="therapy-hours-table"
        loading-text="Caricamento..."
        no-data-text="Nessun paziente disponibile"
      >
        <template v-slot:item.paziente="{ item }">
          <div class="patient-cell">
            <strong>{{ item.pazienteCognome }} {{ item.pazienteNome }}</strong>
            <v-chip v-if="item.privata" x-small label color="grey lighten-3">Privato</v-chip>
          </div>
        </template>

        <template v-slot:item.oreTerapiaSettimanali="{ item }">
          <div class="hours-editor">
            <v-text-field
              v-model="item.draftOreTerapia"
              type="number"
              min="0"
              step="1"
              dense
              outlined
              hide-details
              class="hours-input"
              @input="markDirty(item)"
              @keyup.enter="saveOre(item)"
            ></v-text-field>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  :loading="item.saving"
                  :disabled="!item.dirty || item.saving"
                  v-bind="attrs"
                  v-on="on"
                  @click="saveOre(item)"
                >
                  <v-icon small>mdi-content-save</v-icon>
                </v-btn>
              </template>
              <span>Salva ore settimanali</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.oreAttesePeriodo="{ item }">
          <span>{{ formatHours(expectedHours(item)) }} h</span>
          <span class="muted">({{ formatHours(item.settimaneEquivalenti) }} sett. eq.)</span>
        </template>

        <template v-slot:item.oreTotaliErogate="{ item }">
          <div class="progress-cell">
            <span>{{ formatHours(item.oreTotaliErogate) }} h</span>
            <v-progress-linear
              :value="progressValue(item)"
              :color="progressColor(item.stato)"
              height="8"
              rounded
            ></v-progress-linear>
            <span class="muted">{{ formatPercent(item.percentuale) }}</span>
          </div>
        </template>

        <template v-slot:item.scostamentoOre="{ item }">
          <span :class="deltaClass(item.scostamentoOre)">
            {{ formatSignedHours(item.scostamentoOre) }} h
          </span>
        </template>

        <template v-slot:item.stato="{ item }">
          <v-chip small :color="statusColor(item.stato)" :text-color="statusTextColor(item.stato)">
            {{ statusText(item.stato) }}
          </v-chip>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="weekly-detail-cell">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Settimana</th>
                  <th>Periodo</th>
                  <th class="text-right">Quota</th>
                  <th class="text-right">Attese</th>
                  <th class="text-right">Erogate</th>
                  <th class="text-right">Scostamento</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="week in item.dettagliSettimanali" :key="week.inizioSettimana + '-' + week.fineNelPeriodo">
                  <td>{{ formatDateRange(week.inizioSettimana, week.fineSettimana) }}</td>
                  <td>{{ formatDetailRange(week) }}</td>
                  <td class="text-right">{{ week.giorniNelMese }} gg</td>
                  <td class="text-right">{{ formatHours(week.oreAttese) }} h</td>
                  <td class="text-right">{{ formatHours(week.oreTotali) }} h</td>
                  <td class="text-right" :class="deltaClass(week.scostamentoOre)">
                    {{ formatSignedHours(week.scostamentoOre) }} h
                  </td>
                  <td>
                    <v-chip x-small :color="statusColor(week.stato)" :text-color="statusTextColor(week.stato)">
                      {{ statusText(week.stato) }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <div class="type-breakdown">
              <v-chip x-small label color="teal" text-color="white">
                Terapia {{ formatHours(item.oreTerapiaErogate) }} h
              </v-chip>
              <v-chip x-small label color="light-green lighten-3">
                Supervisione {{ formatHours(item.oreSupervisione) }} h
              </v-chip>
              <v-chip x-small label color="light-blue lighten-3">
                Logopedista {{ formatHours(item.oreLogopedista) }} h
              </v-chip>
              <v-chip x-small label color="blue-grey lighten-4">
                Totali {{ formatHours(item.oreTotaliErogate) }} h
              </v-chip>
            </div>
          </td>
        </template>
      </v-data-table>
    </v-container>
  </v-app>
</template>

<script>
import axios from '@/api/axios';

const ALL_MONTHS = 'ALL';

export default {
  data() {
    const today = new Date();
    return {
      loading: false,
      viewMode: 'cards',
      selectedYear: today.getFullYear(),
      selectedMonths: [today.getMonth() + 1],
      monthItems: this.getMonthItems(),
      years: this.getYears(),
      rows: [],
      headers: [
        { text: 'Paziente', value: 'paziente' },
        { text: 'Ore sett.', value: 'oreTerapiaSettimanali', width: '150px' },
        { text: 'Attese periodo', value: 'oreAttesePeriodo', width: '160px' },
        { text: 'Ore erogate', value: 'oreTotaliErogate', width: '190px' },
        { text: 'Scostamento', value: 'scostamentoOre', width: '130px' },
        { text: 'Stato', value: 'stato', width: '150px' },
        { text: '', value: 'data-table-expand', width: '48px' }
      ],
      numberFormatter: new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    };
  },

  computed: {
    totalExpectedHours() {
      return this.rows.reduce((sum, item) => sum + Number(this.expectedHours(item) || 0), 0);
    },
    totalDeliveredHours() {
      return this.rows.reduce((sum, item) => sum + Number(item.oreTotaliErogate || 0), 0);
    },
    totalDeltaHours() {
      return this.totalDeliveredHours - this.totalExpectedHours;
    },
    totalDeltaColor() {
      if (this.totalDeltaHours < 0) return 'red darken-2';
      if (this.totalDeltaHours > 0) return 'amber darken-3';
      return 'green darken-2';
    },
    periodLabel() {
      if (this.isAllMonthsSelected()) {
        return 'Tutti i mesi ' + this.selectedYear;
      }

      const labels = this.sortedSelectedMonths()
        .map(month => this.monthItems.find(item => item.value === month))
        .filter(Boolean)
        .map(item => item.text);

      if (!labels.length) return 'Nessun mese selezionato';
      return labels.join(', ') + ' ' + this.selectedYear;
    }
  },

  methods: {
    async fetchSummary() {
      const params = this.periodParams();
      if (!params) {
        this.rows = [];
        return;
      }

      this.loading = true;
      try {
        const response = await axios.get('/timesheets/therapy-hours-summary', { params });
        this.rows = response.data.map(this.decorateRow);
      } catch (error) {
        console.error('Errore nel recupero delle ore terapia:', error);
        this.$snackbar('Errore nel recupero delle ore terapia: ' + this.errorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    decorateRow(row) {
      const draft = row.oreTerapiaSettimanali === null || row.oreTerapiaSettimanali === undefined
        ? ''
        : String(row.oreTerapiaSettimanali);
      return {
        ...row,
        draftOreTerapia: draft,
        dirty: false,
        saving: false,
        expanded: false
      };
    },

    markDirty(item) {
      item.dirty = String(item.draftOreTerapia) !== this.savedOreValue(item);
    },

    async saveOre(item) {
      const oreTerapia = this.normalizeOre(item.draftOreTerapia);
      if (oreTerapia === null) {
        this.$snackbar('Inserire un numero intero maggiore o uguale a zero.');
        return;
      }

      item.saving = true;
      try {
        await axios.put(`/pazienti/${item.pazienteId}/ore-terapia`, { oreTerapia });
        this.$snackbar('Ore settimanali salvate per ' + item.pazienteNome + ' ' + item.pazienteCognome + '.');
        await this.fetchSummary();
      } catch (error) {
        console.error('Errore nel salvataggio delle ore terapia:', error);
        this.$snackbar('Errore nel salvataggio delle ore terapia: ' + this.errorMessage(error));
      } finally {
        item.saving = false;
      }
    },

    normalizeOre(value) {
      if (value === '' || value === null || value === undefined) {
        return null;
      }

      const numeric = Number(value);
      if (!Number.isInteger(numeric) || numeric < 0) {
        return null;
      }

      return numeric;
    },

    savedOreValue(item) {
      return item.oreTerapiaSettimanali === null || item.oreTerapiaSettimanali === undefined
        ? ''
        : String(item.oreTerapiaSettimanali);
    },

    onMonthsChange(value) {
      const values = Array.isArray(value) ? value : [];
      if (!values.length) {
        this.resetPeriod();
        return;
      }

      if (values.includes(ALL_MONTHS)) {
        this.selectedMonths = [ALL_MONTHS];
      } else {
        const selected = this.normalizeSelectedMonths(values);
        this.selectedMonths = selected.length === 12 ? [ALL_MONTHS] : selected;
      }

      this.fetchSummary();
    },

    periodParams() {
      if (this.isAllMonthsSelected()) {
        return {
          dataInizio: this.dateValue(this.selectedYear, 1, 1),
          dataFine: this.dateValue(this.selectedYear, 12, 31)
        };
      }

      const selected = this.sortedSelectedMonths();
      if (!selected.length) {
        return null;
      }

      const firstMonth = selected[0];
      const lastMonth = selected[selected.length - 1];
      return {
        dataInizio: this.dateValue(this.selectedYear, firstMonth, 1),
        dataFine: this.dateValue(this.selectedYear, lastMonth, this.lastDayOfMonth(this.selectedYear, lastMonth)),
        mesiSelezionati: selected.join(',')
      };
    },

    expectedHours(item) {
      if (item.oreAttesePeriodo !== null && item.oreAttesePeriodo !== undefined) {
        return item.oreAttesePeriodo;
      }
      return item.oreAtteseMese;
    },

    resetPeriod() {
      const today = new Date();
      this.selectedYear = today.getFullYear();
      this.selectedMonths = [today.getMonth() + 1];
      this.fetchSummary();
    },

    sortedSelectedMonths() {
      return this.normalizeSelectedMonths(this.selectedMonths);
    },

    normalizeSelectedMonths(months) {
      return [...new Set(months.map(Number))]
        .filter(month => month >= 1 && month <= 12)
        .sort((a, b) => a - b);
    },

    isAllMonthsSelected() {
      return this.selectedMonths.includes(ALL_MONTHS);
    },

    getMonthItems() {
      const months = Array.from({ length: 12 }, (_, index) => {
        const date = new Date(2026, index, 1);
        return {
          value: index + 1,
          text: date.toLocaleString('it-IT', { month: 'long' })
        };
      });

      return [
        { value: ALL_MONTHS, text: 'Tutti' },
        ...months
      ];
    },

    getYears() {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 8 }, (_, index) => currentYear - index);
    },

    dateValue(year, month, day) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    },

    lastDayOfMonth(year, month) {
      return new Date(year, month, 0).getDate();
    },

    formatHours(value) {
      return this.numberFormatter.format(Number(value || 0));
    },

    formatSignedHours(value) {
      const numeric = Number(value || 0);
      if (numeric > 0) return '+' + this.formatHours(numeric);
      return this.formatHours(numeric);
    },

    formatPercent(value) {
      return this.numberFormatter.format(Number(value || 0)) + '%';
    },

    formatDateRange(start, end) {
      if (!start || !end) return '';
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    },

    formatDetailRange(week) {
      return this.formatDateRange(
        week.inizioNelPeriodo || week.inizioNelMese,
        week.fineNelPeriodo || week.fineNelMese
      );
    },

    formatDate(value) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit'
      });
    },

    progressValue(item) {
      return Math.min(Number(item.percentuale || 0), 100);
    },

    progressColor(stato) {
      if (stato === 'SOTTO_PREVISIONE') return 'red';
      if (stato === 'SOPRA_PREVISIONE') return 'amber darken-2';
      if (stato === 'ORE_NON_IMPOSTATE') return 'grey';
      return 'green';
    },

    statusColor(stato) {
      if (stato === 'SOTTO_PREVISIONE') return 'red lighten-4';
      if (stato === 'SOPRA_PREVISIONE') return 'amber lighten-4';
      if (stato === 'ORE_NON_IMPOSTATE') return 'grey lighten-3';
      return 'green lighten-4';
    },

    statusTextColor(stato) {
      if (stato === 'SOTTO_PREVISIONE') return 'red darken-4';
      if (stato === 'SOPRA_PREVISIONE') return 'amber darken-4';
      if (stato === 'ORE_NON_IMPOSTATE') return 'grey darken-3';
      return 'green darken-4';
    },

    statusText(stato) {
      if (stato === 'SOTTO_PREVISIONE') return 'Sotto previsione';
      if (stato === 'SOPRA_PREVISIONE') return 'Sopra previsione';
      if (stato === 'ORE_NON_IMPOSTATE') return 'Ore non impostate';
      return 'In linea';
    },

    statusClass(stato) {
      return {
        'status-low': stato === 'SOTTO_PREVISIONE',
        'status-high': stato === 'SOPRA_PREVISIONE',
        'status-missing': stato === 'ORE_NON_IMPOSTATE',
        'status-ok': stato === 'IN_LINEA'
      };
    },

    deltaClass(value) {
      const numeric = Number(value || 0);
      return {
        'delta-negative': numeric < 0,
        'delta-positive': numeric > 0,
        'delta-neutral': numeric === 0
      };
    },

    errorMessage(error) {
      const data = error && error.response ? error.response.data : null;
      if (data && data.message) return data.message;
      if (data && data.error) return data.error;
      if (error && error.message) return error.message;
      return String(error);
    }
  },

  mounted() {
    this.fetchSummary();
  }
};
</script>

<style scoped>
.therapy-hours-page {
  background: #f5f7f8;
  min-height: 100vh;
}

.toolbar-panel {
  background: #ffffff;
  border: 1px solid #dbe3e5;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 14px;
}

.toolbar-actions,
.card-footer,
.hours-editor,
.type-breakdown {
  display: flex;
  align-items: center;
  gap: 8px;
}

.months-select {
  min-width: 260px;
}

.selection-more {
  color: #5d6b70;
  font-size: 0.82rem;
  padding-left: 4px;
}

.summary-chips {
  gap: 8px;
}

.toolbar-actions {
  justify-content: flex-end;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
}

.patient-card {
  border-radius: 8px;
  border-left-width: 5px;
  padding: 14px;
}

.status-low {
  border-left-color: #d32f2f;
}

.status-high {
  border-left-color: #f9a825;
}

.status-ok {
  border-left-color: #2e7d32;
}

.status-missing {
  border-left-color: #9e9e9e;
}

.card-header,
.card-metrics {
  display: grid;
  gap: 10px;
}

.card-header {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
}

.card-metrics {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 14px;
}

.metric {
  background: #f7faf9;
  border: 1px solid #e2eaec;
  border-radius: 6px;
  padding: 10px;
  min-width: 0;
}

.metric span,
.metric small {
  display: block;
  color: #5d6b70;
}

.metric strong {
  display: block;
  font-size: 1.15rem;
  line-height: 1.35;
}

.card-progress {
  margin-top: 12px;
}

.card-footer {
  justify-content: space-between;
  margin-top: 12px;
}

.therapy-hours-table {
  background: #ffffff;
  margin-top: 12px;
}

.patient-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
}

.hours-editor {
  width: 150px;
}

.hours-input {
  max-width: 98px;
}

.progress-cell {
  display: grid;
  grid-template-columns: 56px minmax(72px, 1fr) 48px;
  gap: 8px;
  align-items: center;
  min-width: 168px;
}

.muted {
  color: #6b7280;
  font-size: 0.82rem;
}

.detail-panel,
.weekly-detail-cell {
  background: #f7faf9;
}

.detail-panel {
  border-top: 1px solid #e2eaec;
  margin-top: 12px;
  padding-top: 10px;
  overflow-x: auto;
}

.weekly-detail-cell {
  padding: 12px 24px !important;
}

.type-breakdown {
  flex-wrap: wrap;
  margin-top: 10px;
}

.delta-negative {
  color: #c62828;
  font-weight: 600;
}

.delta-positive {
  color: #8a5a00;
  font-weight: 600;
}

.delta-neutral {
  color: #2e7d32;
  font-weight: 600;
}

@media (max-width: 720px) {
  .toolbar-actions {
    justify-content: flex-start;
  }

  .cards-grid {
    grid-template-columns: 1fr;
  }

  .card-metrics {
    grid-template-columns: 1fr;
  }

  .progress-cell {
    grid-template-columns: 1fr;
    gap: 4px;
    min-width: 120px;
  }

  .weekly-detail-cell {
    padding: 8px !important;
  }
}
</style>
